import { useState } from "react";
import { Link } from "wouter";
import { useListSessions, useDeleteSession } from "@workspace/api-client-react";
import { Card, Button, Input, Badge } from "@/components/ui";
import { Search, Trash2, FileText, File, Type, Calendar, X, Loader2, Youtube, Video, Music } from "lucide-react";
import { formatDate } from "@/lib/utils";

export default function History() {
  const [search, setSearch] = useState("");
  const { data: sessions, isLoading, refetch } = useListSessions({ sort: "lastOpened" });
  const { mutate: deleteSession, isPending } = useDeleteSession({ onSuccess: () => refetch() });
  const [deleteId, setDeleteId] = useState<{ id: number; title: string } | null>(null);

  const filteredSessions = sessions?.filter(s => 
    s.title.toLowerCase().includes(search.toLowerCase())
  );

  const handleConfirmDelete = () => {
    if (!deleteId) return;
    deleteSession({ id: deleteId.id }, { onSuccess: () => setDeleteId(null) });
  };

  const getSourceIcon = (type: string) => {
    switch (type) {
      case "pdf":     return <File className="w-4 h-4" />;
      case "txt":     return <FileText className="w-4 h-4" />;
      case "youtube": return <Youtube className="w-4 h-4" />;
      case "video":   return <Video className="w-4 h-4" />;
      case "audio":   return <Music className="w-4 h-4" />;
      default:        return <Type className="w-4 h-4" />;
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 animate-in fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold font-display">Study History</h1>
          <p className="text-muted-foreground mt-1">Review and manage your past study materials.</p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input 
            placeholder="Search sessions..." 
            className="pl-10 rounded-full"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {!isLoading && filteredSessions?.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <p className="text-muted-foreground text-lg">No sessions found.</p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredSessions?.map((session) => (
            <Link key={session.id} href={`/sessions/${session.id}`}>
              <Card className="p-4 sm:p-6 hover:shadow-md hover:border-primary/30 transition-all cursor-pointer group flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start sm:items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    {getSourceIcon(session.sourceType)}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold font-display group-hover:text-primary transition-colors">
                      {session.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(session.createdAt)}
                      </span>
                      {session.uploadedFileName && (
                        <span className="max-w-[200px] truncate">&bull; {session.uploadedFileName}</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 self-end sm:self-auto w-full sm:w-auto justify-between sm:justify-end">
                  <Badge variant={session.hasOutputs ? "default" : "accent"}>
                    {session.hasOutputs ? "Ready" : "Draft"}
                  </Badge>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteId({ id: session.id, title: session.title }); }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <Card className="w-full max-w-sm p-6 shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-destructive/10 rounded-lg flex items-center justify-center">
                  <Trash2 className="w-4 h-4 text-destructive" />
                </div>
                <h2 className="text-lg font-bold font-display">Delete File</h2>
              </div>
              <button onClick={() => setDeleteId(null)} className="text-muted-foreground hover:text-foreground transition-colors rounded-lg p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              This will permanently delete &ldquo;{deleteId.title}&rdquo; and all its study materials. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setDeleteId(null)}>Cancel</Button>
              <Button type="button" variant="destructive" className="flex-1" onClick={handleConfirmDelete} disabled={isPending}>
                {isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Delete
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
